import { Bet, BetOffer, BetSlipShare } from "@models";
import i18n from "@state/services/localizationService";

import { CashoutType } from "@gp/hub";
import { BetSlipStatus, BetStatus } from "@utils/common";
import MyBetsStore from "@state/stores/my-bets/MyBetsStore";
import BetDetailsStore from "@state/stores/my-bets/BetDetailsStore";
import MyBetsViewStore from "@state/stores/my-bets/MyBetsViewStore";
import { ObservableMap } from "mobx";
import { ActiveEventsStore } from "@state/stores/my-bets/ActiveEventsStore";
import { BetSlipExtended, BetSlipForShare } from "@api-types/agency/bet-slip";
import { CombinationOffer } from "@api-types/bet-slip/CombinationOffer";

export function checkDecimals(inputValue: string) {
	return /^\d*\.?\d{0,2}$/.test(inputValue);
}

/**
 * adds displayedAmount and indicator to bet
 * Adds is multi and multiEventName to bet.betSlipOffers
 * @params {object} bet
 */
export function mapMissingPropertyToBet(bet: Bet) {
	const offerGroupsByEvent = new Map();

	for (const offer of bet.betSlipOffers) {
		const eventId = offer.eventId;

		if (!offerGroupsByEvent.has(eventId)) {
			offerGroupsByEvent.set(eventId, []);
		}

		offerGroupsByEvent.get(eventId).push(offer);
	}

	const mappedOffers = [];
	for (const eventOffers of Array.from(offerGroupsByEvent.values())) {
		if (eventOffers.length > 1) {
			for (let i = 1; i < eventOffers.length; i++) {
				eventOffers[i].isMulti = true;
				eventOffers[i].multiEventName = `${i + 1} Multi`;
			}
		}

		mappedOffers.push(...eventOffers);
	}

	bet.betSlipOffers = mappedOffers;
	bet.displayedAmount = bet.calculatedCashoutAmount?.toFixed(2);
	bet.indicator = 0;
}

/**
 * @param {array} bets list of bets
 * @returns {array} of unique event ids
 */
export function getEventIdsFromMyBets(
	bets: {
		betSlipOffers: BetOffer[];
	}[]
): string[] {
	const eventIds = new Set<string>();
	bets?.forEach((bet) => {
		bet?.betSlipOffers?.forEach((offer) => {
			if (offer.eventId) {
				eventIds.add(offer.eventId);
			}
		});
	});

	return Array.from(eventIds.values());
}

/**
 * @parm {number} number value for which to get indicator className name
 * @returns {string} indicator className name
 */
export function repIndicCss(number: number | null | undefined) {
	if (number == null) {
		return "";
	}

	if (number < 0) {
		return "u-color--sent--danger";
	}

	if (number > 0) {
		return "u-color--sent--success";
	}

	return "";
}

export function getCombinationStatus(statusNumber: number) {
	switch (statusNumber) {
		case 1:
			return "active";
		case 2:
			return "win";
		case 3:
			return "lose";
	}
	return "";
}

export function getSystemIdentifierStringFromOffer(
	combinationOffers: CombinationOffer[]
) {
	return combinationOffers.map((o) => o.systemIdentifier).join("");
}

export function getSystemIdentifierString(
	combinationOffers: { systemIdentifier: string | string[] }[]
) {
	return combinationOffers.map((o) => o.systemIdentifier).join("");
}

export function getFormatedCombinationQuota(quota: number) {
	if (quota < 100) {
		return quota.toFixed(2);
	}
	return quota.toFixed();
}

export function startPrint(betId: string) {
	const element = document.querySelector(`#bet-item-${betId}`);

	if (!element) {
		console.error("Element not found");
		return;
	}

	const printContent = element.innerHTML;
	const iframe = document.createElement("iframe");

	iframe.style.visibility = "hidden";

	document.body.appendChild(iframe);

	const doc = iframe.contentWindow?.document;

	if (doc) {
		doc.open();

		let htmlContent = `
			<html>
			<head>
			<title>${i18n.t("COMMON.MY_BETS_TITLE")}</title>
			<style>

				.card--bet__ticket {
					display: flex;
					flex-direction: column;
					align-items: flex-start;
					justify-content: center;
					min-width: 25%;
				}

				.card--bet__tags {
					display: flex;
					align-items: center;
					gap: 0 6px;
				}

				.card--bet__number {
					margin-top: 3px;
					font-size: 13px;
					line-height: 24px;
				}

				a {
					text-decoration: none;
					-webkit-text-decoration-skip: ink;
					text-decoration-skip-ink: auto;
					color: inherit;
				}

				.btn {
					display: none;
				}

				.u-type--wgt--bold {
					font-weight: 700;
				}

				.u-padd--reset {
					padding: 0 !important;
				}

				.widget--bets__multi {
					position: relative;
					padding-left: 12px;
					font-size: 11px;
					line-height: 18px;
				}

				.widget--bets__multi::before {
					content: "";
					position: absolute;
					top: 0;
					left: 5px;
					bottom: 0;

					border-left: 1px dashed black;
				}

				.widget--bets__pick {
					display: flex;
					align-items: flex-start;
				}

				.widget--bets__type, .widget--bets__tip {
					display: inline;
				}

				.table--bets {
					overflow: hidden;
					width: 100%;
					margin: 24px 0;

					font-size: 12px;
					line-height: 18px;

					border: 1px solid black;
					border-radius: 3px;
				}

				.table--bets--reset {
					overflow: hidden;
					width: 100%;

					font-size: 12px;
					line-height: 18px;

					border: 0 !important;
					border-collapse: collapse;
				}

				.table--bets__th, 
				.table--bets__ts, 
				.table--bets__td, 
				.table--bets__dd,
				.table--bets__tf{
					overflow: hidden;
					vertical-align: text-top;
					max-width: 0px;
					font-weight: 400;
				}

				.table--bets__th, 
				.table--bets__ts, 
				.table--bets__td,
				.table--bets__tf {
					padding: 6px;
				}

				.table--bets__head {
					font-weight: 400;
				}

				.table--bets__th {
					text-align: left;
					border-bottom: 1px solid black;
				}

				.table--bets__th--time, 
				.table--bets__th--price {
					width: 20%;
				}

				.table--bets__th--type {
					width: 25%;
				}

				.table--bets__th--print {
					width: 9%;
				}

				.table--bets__th span {
					display: block;
				}

				.table--bets__subhead {
					border-bottom: 1px solid black;
				} 

				.table--bets__event {
					display: flex;
    				gap: 6px;
				}

				.table--bets__ts {
					font-weight: 700;
    				text-align: left;
					border-bottom: 1px solid black;
				}

				.table--bets__ts--time {
					width: 132px;
    				min-width: 132px;
				}
					
				.table--bets__ts--event {
					min-width: 34%;
				}
					
				.table--bets__ts--bank {
					width: 24px;
    				min-width: 24px;
				}
					
				.table--bets__ts--bet-type {
					width: 22%;
				}
					
				.table--bets__ts--odds {
					width: 10%;
				}
					
				.table--bets__ts--results {
					width: 20%;
				}

				.table--bets__ts--icon {
					display: none;
				} 

				.table--bets__body {
					border-bottom: 1px solid black;
				}
					
				.table--bets__tr {
					border-top: 1px solid black;
				}
					
				.table--bets__td--time {
					text-align: center;
				}

				.table--bets__td--event, 
				.table--bets__td--bet-type {
					overflow: hidden;
				}

				.table--bets__td--bet-type {
					vertical-align: middle;
				}
					
				.table--bets__td--bank {
					vertical-align: top;
				}

				.table--bets__td--icon {
					display: none;
				}
					
				.table--bets__ts, 
				.table--bets__td {
					text-align: left;
					border-right: 1px solid black;
				}
					
				.table--bets__ts:last-child, 
				.table--bets__ts--combi, 
				.table--bets__ts--bank, 
				.table--bets__ts--results, 
				.table--bets__ts--icon, 
				.table--bets__td:last-child, 
				.table--bets__td--combi, 
				.table--bets__td--bank, 
				.table--bets__td--results, 
				.table--bets__td--icon {
					border-right: 0 !important;
				}
					
				.table--bets__ts--time, 
				.table--bets__ts--combi, 
				.table--bets__ts--odds, 
				.table--bets__ts--icon, 
				.table--bets__td--time, 
				.table--bets__td--combi, 
				.table--bets__td--odds, 
				.table--bets__td--icon {
					text-align: center;
				}
					
				.table--bets__ts--combi, 
				.table--bets__td--combi {
					width: 27px;
					min-width: 27px;
					font-weight: 500;
				}
					
				.table--bets__ts--bank, 
				.table--bets__td--bank {
					padding-right: 0px;
				}
					
				.table--bets__ts--odds, 
				.table--bets__td--odds {
					font-weight: 700;
				}

				.table--bets__live-score {
					font-size: 11px;
    				line-height: 18px;
				}
					
				.table--bets__result {
					display: flex;
					justify-content: space-between;
					white-space: normal;
					word-break: break-word;
				}
					
				.table--bets__result__ft {
					width: 60px;
    				font-weight: 500;
				}
					
				.table--bets__result__ht {
					flex-grow: 1;
					margin-left: 6px;
					font-size: 11px;
					line-height: 18px;
				}
					
				.table--bets__dd {
					padding: 6px 12px;

					border-top: 1px solid black;
				}

				.table--bets__dd:last-child {
					font-weight: 500;
					text-align: right;
				}

				.table--bets__fiscalization {
					font-family: monospace;
				}
					
				.table--bets__tr.table--bets__fiscalization {
					border-top: none;
				}

				.table--bets__tr.table--bets__fiscalization.table--bets__fiscalization--dashed {
					 border-top: 1px dashed black;
				}

				.table--bets__tf {
					padding-top: 3px;
					padding-bottom: 3px;

					word-break: break-all;
				}

				.table--bets__tf:last-child {
					font-weight: 400;
					text-align: left;
				}

				.table--bets__footer {
					display: none;
				}

				.table--bets__dd.u-padd--x--xtny {
					display: none;
				}

				.u-mar--right--xtny {
					margin-right: 6px;
				}
				
			</style>
			</head>
			<body>
			<div class="container">
				${printContent}
			</div>
			</body>
			</html>
			`;

		doc.write(htmlContent);
		doc.close();

		const btnElements = doc.querySelectorAll(".btn");

		btnElements.forEach((btn) => {
			btn.classList.add("no-print");
		});

		iframe.contentWindow.focus();
		iframe.contentWindow.print();

		setTimeout(() => {
			document.body.removeChild(iframe);
		}, 1000);
	}
}

export function shouldShowBetActions(
	cType: CashoutType,
	betStatusAbrv: BetStatus | string,
	displayedAmount: string
) {
	return (
		cType !== CashoutType.JACKTIME ||
		betStatusAbrv !== BetStatus.OPEN ||
		displayedAmount === null
	);
}

export function hasActiveEvents(bet: Bet | BetSlipShare) {
	return (
		(bet.betSlipStatus.abrv?.toLowerCase() === BetSlipStatus.SUBMITTED ||
			bet.betSlipStatus.abrv?.toLowerCase() === BetSlipStatus.LOST) &&
		bet.betSlipOffers.some(
			(offer) =>
				offer.bettingOfferStatus == 1 ||
				(offer.multiOffers &&
					offer.multiOffers.every(
						(mOffer) => mOffer.bettingOfferStatus == 1
					))
		)
	);
}

export function checkIfIsMulti(
	offer: BetOffer,
	store: {
		activeEventsStore: ActiveEventsStore;
	} | null,
	cssClass: string,
	groupHasTitle?: boolean
) {
	if (offer.isMulti || groupHasTitle) {
		return { time: "", cssClass: "" };
	}
	if (store != null) {
		if (store.activeEventsStore.eventsMap.has(offer.eventId)) {
			if (
				store.activeEventsStore.eventsMap.get(offer.eventId)?.matchTime
					?.displayTime
			) {
				return {
					time: store.activeEventsStore.eventsMap.get(offer.eventId)
						?.matchTime?.displayTime,
					cssClass: " " + cssClass,
				};
			}
			return {
				time: offer.timeString,
				cssClass: "",
			};
		}
	}

	return { time: offer.timeString, cssClass: "" };
}

export function injectIsMultiToOffers(offerGroupsByEvent: BetOffer[][]) {
	for (const eventOffers of offerGroupsByEvent) {
		if (eventOffers.length > 1) {
			for (let i = 1; i < eventOffers.length; i++) {
				eventOffers[i].isMulti = true;
				eventOffers[i].multiEventName = `${i + 1} Multi`;
			}
		}
	}
}

export function groupOffersByEvent(offers: BetOffer[]): BetOffer[][] {
	const offerGroupsByEvent = new Map();

	for (const offer of offers) {
		const eventId = offer.eventId;

		if (!offerGroupsByEvent.has(eventId)) {
			offerGroupsByEvent.set(eventId, []);
		}

		offerGroupsByEvent.get(eventId).push(offer);
	}

	return Array.from(offerGroupsByEvent.values());
}

export function mapBetSlipOffers(bet: BetSlipExtended | BetSlipForShare | Bet) {
	bet?.betSlipOffers?.forEach((offer) => {
		if (
			offer?.specifier?.player == null ||
			![
				"player-total-assists",
				"player-total-rebounds",
				"player-total-3-point-field-goals",
				"player-total-points",
				"player-total-rushing-yards",
				"player-total-passing-touchdowns",
				"player-total-passing-yards",
				"player-total-receiving-yards",
			].includes(offer.sportData.bettingTypeAbrv)
		) {
			return;
		}

		offer.specifier.player =
			offer.playerFirstName +
			" " +
			offer.playerLastName +
			` (${
				offer.sportData.teamOneId === offer.teamId
					? offer.sportData.teamOneName
					: offer.sportData.teamTwoName
			})`;
		offer.playerId = null;
		offer.teamId = null;
	});
}
